import { CloseButton, Combobox, Input, Pill, PillsInput, rem, UnstyledButton, useCombobox } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import AbsoluteError from 'components/ui/AbsoluteError';
import { FieldSelectMultiStore } from 'stores/local/shared/form/FieldSelectStore';
import { preventDefault } from 'utils/preventDefault';

import OptionsDropdown from '../FieldSelect/OptionsDropdown';

import SummaryValue, { FormatValue } from './SummaryValue';
import Value from './Value';

import s from './FieldMultiSelect.module.scss';

type FieldMultiSelectProps = {
  className?: string;
  placeholder?: string;
  placeholderClassName?: string;
  size?: string;
  label?: string;
  maxDisplayedValues?: number;
  /** pills — выбранные значения пиллами; checkboxes — опции с чекбоксами, в поле одна строка через formatValue */
  variant?: 'pills' | 'checkboxes';
  formatValue?: FormatValue;
  /** Фокус в поиск при открытии. На телефоне открывает клавиатуру — для коротких списков лучше выключать */
  autoFocusSearch?: boolean;
  /** Максимальная высота списка опций в px */
  dropdownMaxHeight?: number;
  store: FieldSelectMultiStore;
};

const FieldMultiSelect: React.FC<FieldMultiSelectProps> = ({
  store,
  placeholder,
  placeholderClassName,
  size,
  label,
  className,
  maxDisplayedValues,
  variant = 'pills',
  formatValue,
  autoFocusSearch = true,
  dropdownMaxHeight,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      store.blur();
      store.resetSearch();
    },

    onDropdownOpen: () => {
      store.focus();

      if (autoFocusSearch) {
        combobox.focusSearchInput();
      }
    },
  });

  const onInputClick = React.useCallback(() => {
    combobox.toggleDropdown();
  }, [combobox]);

  const onOptionSubmit = React.useCallback(
    (value: string) => {
      runInAction(() => {
        if (store.valueKeysSet.has(value)) {
          store.removeByKey(value);

          return;
        }

        const option = store.optionsMap[value];

        if (option) {
          store.setValue([...store.value, option]);
        }
      });
    },
    [store],
  );

  const onResetValue = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      store.resetValue();
    },
    [store],
  );

  const notEmpty = store.value.length > 0;
  const withCheckboxes = variant === 'checkboxes';

  return (
    <div className={className}>
      <Combobox store={combobox} size="sm" keepMounted={false} withinPortal={true} onOptionSubmit={onOptionSubmit}>
        <Combobox.Target>
          <PillsInput
            label={label}
            onClick={onInputClick}
            error={store.hasError && <AbsoluteError error={store.error} />}
            size={size ? size : 'lg'}
            pointer
            data-dropdown-opened={combobox.dropdownOpened}
            classNames={{
              wrapper: !withCheckboxes && combobox.dropdownOpened ? s.dropdownOpened : '',
              input: withCheckboxes ? s.root__summaryInput : undefined,
            }}
            rightSectionPointerEvents={notEmpty && !withCheckboxes ? undefined : 'none'}
            rightSection={
              notEmpty && !withCheckboxes ? (
                <CloseButton size="sm" onMouseDown={preventDefault} onClick={onResetValue} aria-label="Сбросить все" />
              ) : (
                <IconChevronDown />
              )
            }
            styles={{
              input: {
                paddingLeft: 16,
              },
            }}
          >
            <Pill.Group gap={rem(4)} className={withCheckboxes ? s.root__summary : undefined}>
              {!notEmpty && <Input.Placeholder className={placeholderClassName}>{placeholder}</Input.Placeholder>}
              {notEmpty && withCheckboxes && <SummaryValue store={store} formatValue={formatValue} />}
              {notEmpty && !withCheckboxes && <Value store={store} maxDisplayedValues={maxDisplayedValues} />}
            </Pill.Group>
          </PillsInput>
        </Combobox.Target>

        <OptionsDropdown
          store={store}
          withCheckboxes={withCheckboxes}
          maxHeight={dropdownMaxHeight}
          optionsFooter={
            withCheckboxes && (
              <UnstyledButton className={s.root__reset} disabled={!notEmpty} onClick={store.resetValue}>
                Сбросить выбор
              </UnstyledButton>
            )
          }
        />
      </Combobox>
    </div>
  );
};

export default observer(FieldMultiSelect);
